import React, { useContext, useState, useEffect } from 'react';
import { useQuery } from 'react-apollo';
import { BasePage, Helmet, StaticContext, SkeletonLoader } from '@sm/webassets';
import { t, defineMessages } from '@sm/intl';
import { LANGUAGES_BY_CODE } from '@sm/locale';
import { ThemeProvider } from 'react-jss';
import { Box } from '@wds/box';
import theme from '~app/styles/cmsTheme';
import GetCollectorTypeByIdQuery from '~app/pages/SurveyThanks/GetCollectorTypeById.graphql';

import EndpageTemplates from './EndpageTemplates';
import CovidEndpage from './CovidEndpage';
import Embedded from './Embedded';
import EmbeddedNonBranded from './EmbeddedNonBranded';
import NonBranded from './NonBranded';
import RebrandEpForm from './RebrandEpForm';
import RebrandEpFormIntl from './RebrandEpFormIntl';
import RebrandExistingUser from './RebrandExistingUser';
import Research from './Research';

const COPY = defineMessages({
  DEFAULT_THANKS_MESSAGE: {
    id: 'SurveyThanks.thanksMessage',
    defaultMessage: 'Thank you for taking this survey.',
  },
  META_KEYWORDS: {
    id: 'SurveyThanks.metaKeywords',
    defaultMessage:
      'questionnaire, questionnaires, questionaire, questionaires, free online survey, free online surveys',
  },
  THANKS_PAGE_TITLE: {
    id: 'SurveyThanks.title',
    defaultMessage: 'Thank you! Create Your Own Online Survey Now! | SurveyMonkey',
  },
});

function SurveyThanks(): React.ReactElement {
  const {
    pageOptions: {
      canonical,
      collectorId,
      isBasicEndpageGroup,
      isNonBranded = false,
      isWhiteLabel = false,
      isGovernment = false,
      isReleaseToggle = false,
      randomNumber,
      languageId: surveyLangId,
    },
    environment: { countryCode },
    userInfo: { isExistingUser },
  } = useContext(StaticContext);

  const [showFooter, setShowFooter] = useState(!isNonBranded);
  const isNonEnglish = parseInt(surveyLangId, 10) !== LANGUAGES_BY_CODE.en.id;
  const isUSA = countryCode.toUpperCase() === 'US';

  const { loading, error, data } = useQuery(GetCollectorTypeByIdQuery, {
    context: {
      targetPath: '/lo-graphql/collector',
      headers: {
        'X-Collector-Id': collectorId,
        'X-Language-Id': surveyLangId,
      },
    },
    variables: {
      collectorId,
      surveyLangId,
    },
    errorPolicy: 'all',
  });

  useEffect(() => {
    if (data?.collector === null || data?.collector?.type === 'POPUP') {
      setShowFooter(false);
    }
  }, [data]);

  let EndPage: React.ReactElement;
  switch (true) {
    case isReleaseToggle:
      EndPage = <EndpageTemplates />;
      break;
    case data?.collector.type === 'POPUP':
      EndPage = isNonBranded ? <EmbeddedNonBranded /> : <Embedded />;
      break;
    case isGovernment:
      EndPage = <NonBranded />;
      break;
    case isWhiteLabel:
      EndPage = <Research />;
      break;
    case isNonBranded:
      EndPage = <NonBranded />;
      break;
    case isNonEnglish:
      EndPage = <RebrandEpFormIntl />;
      break;
    case isBasicEndpageGroup:
      EndPage = <RebrandEpForm />;
      break;
    case isExistingUser:
      EndPage = isUSA && randomNumber <= 0.5 ? <CovidEndpage /> : <RebrandExistingUser />;
      break;
    case isUSA && randomNumber <= 0.35:
      EndPage = <CovidEndpage />;
      break;
    default:
      EndPage = <RebrandEpForm />;
      break;
  }

  return (
    <ThemeProvider theme={theme}>
      <BasePage includeHeader={false} includeFooter={showFooter} pageId="Survey Thanks" legacyWeb="anonweb">
        <Helmet>
          <base target="_top" href="/" />
          <meta name="keywords" content={t(COPY.META_KEYWORDS)} />
          <meta name="description" content={t(COPY.DEFAULT_THANKS_MESSAGE)} />
          <link rel="canonical" href={canonical} />
          <title>{t(COPY.THANKS_PAGE_TITLE)}</title>
        </Helmet>
        {!isReleaseToggle && (error || data?.collector === null) && <NonBranded />}
        {loading && (
          <Box display="flex" flexJustify="center" flexAlign="center" p={5}>
            <SkeletonLoader height={130} width={400}>
              <circle cx="10" cy="20" r="8" />
              <rect x="25" y="15" rx="5" ry="5" width="350" height="10" />
            </SkeletonLoader>
          </Box>
        )}
        {(isReleaseToggle || data?.collector) && EndPage}
      </BasePage>
    </ThemeProvider>
  );
}

export default SurveyThanks;
